"use client";

import {useRouter} from "next/navigation";
import {createContext, useContext, useEffect, useState} from "react";
import {ClientSessionData} from "./session-types";
import {apiClient} from "@/lib/api-client";
import {createBasepathPath, createBasepathUrl} from "@/lib/utils";
import {broadcastAuthEvent, listenForAuthEvents} from "@/lib/cross-tab-sync";
import {logInfo, logError} from '@/lib/logging';

export type UserState = ClientSessionData | null;

/**
 * Module-level user context cache for logging
 * Updated by AuthProvider whenever user state changes
 */
let currentUserContext: {authId?: string; sessionId?: string} = {};

/**
 * Get current user context for logging (called by logging.ts)
 */
export function getUserContextForLogging(): {authId?: string; sessionId?: string} {
  return currentUserContext;
}

type AuthContextType = {
  user: UserState;
  status: "loading" | "authenticated" | "unauthenticated";
  signIn: () => void;
  signOut: () => void;
};

// Create the authentication context
const AuthContext = createContext<AuthContextType>({
  user: null,
  status: "loading",
  signIn: () => {
  },
  signOut: () => {
  },
});

// Authentication provider props
type AuthProviderProps = {
  children: React.ReactNode;
  /**
   * Whether to require authentication for this route.
   * - true: Redirect unauthenticated users to login (default for protected routes)
   * - false: Allow unauthenticated access (for public routes)
   */
  requireAuth?: boolean;
};

/**
 * Authentication provider for Auth0 SDK integration
 *
 * Auth0 SDK handles all authentication automatically via middleware:
 * - Token storage in secure HTTP-only cookies
 * - Token refresh on every request (rolling sessions)
 * - Session validation through middleware
 *
 * This provider only tracks user state for client-side UI rendering.
 * Auth middleware protects routes - this is just for display purposes.
 *
 * @param requireAuth - If true, redirects unauthenticated users to login. Default: true
 */
export function AuthProvider({children, requireAuth = true}: AuthProviderProps) {
  const [user, setUser] = useState<UserState>(null);
  const [status, setStatus] = useState<"loading" | "authenticated" | "unauthenticated">("loading");
  const router = useRouter();

  const signIn = () => {
    if (!window.location.pathname.startsWith(createBasepathPath("/login"))) {
      // Redirect to our login page
      // Use window.location.replace() to prevent potential ChunkLoadError
      const loginUrl = createBasepathUrl("/auth/login");
      window.location.replace(loginUrl);
    }
  };

  const signOut = async () => {
    // Clear user context cache for logging
    currentUserContext = {};

    // Broadcast logout event to other tabs before proceeding
    // This ensures other tabs are notified even if the redirect happens immediately
    broadcastAuthEvent('logout');

    // DELETE user session from API
    await apiClient.fetch("/api/auth/session", {method: 'DELETE'})
      .catch(err => {
        // Log error but continue with sign-out
        logError(err instanceof Error ? err : new Error(String(err)), {
            message: "Error during sign-out session revocation"
        });
      });

    // Redirect to Auth0 logout endpoint to sign the user out, and then redirect them back to the login page
    // Use window.location.replace() instead of .href to immediately stop script execution
    // and prevent ChunkLoadError during logout (Next.js tries to load chunks after session is invalidated)
    const homeUrl = createBasepathUrl("/");
    window.location.replace(createBasepathPath(`/auth/logout?returnTo=${homeUrl}`));
  };


  // Initialize user state on mount
  useEffect(() => {
    const initialize = async () => {
      const {sessionData: userState, code} = await apiClient.initializeUserState();

      // valid user state, user is authenticated
      if (userState) {
        // Set user context for logging
        currentUserContext = {
          authId: userState.user.authId,
        };

        logInfo({
            message: "User is authenticated",
            userId: userState.user.id
        });
        setUser(userState);
        setStatus("authenticated");

        // else, if session has been invalidated, log them out and redirect to login page
      } else if (code === 'session_invalidated') {
        currentUserContext = {};
        setUser(null);
        setStatus("unauthenticated");

        // Only sign out (which redirects to Auth0) if authentication is required
        // On public pages, just set unauthenticated status without redirecting
        if (requireAuth) {
          await signOut();
        }

        // else, user is completely unauthenticated
      } else {
        currentUserContext = {};
        logInfo("User is unauthenticated");
        setUser(null);
        setStatus("unauthenticated");

        // Only redirect to sign-in if authentication is required
        if (requireAuth) {
          signIn(); // Redirect to sign-in
        }
      }
    };

    initialize();
  }, [router, requireAuth]);

  // Listen for logout events from other tabs
  useEffect(() => {
    const cleanup = listenForAuthEvents((event) => {
      if (event.type === 'logout') {
        currentUserContext = {};
        logInfo('Logout event received from another tab');
        // Update state to unauthenticated
        setUser(null);
        setStatus('unauthenticated');
        // Only redirect to login page if authentication is required
        // Use window.location.replace() to prevent ChunkLoadError during logout
        if (requireAuth) {
          const loginUrl = createBasepathUrl('/auth/login');
          window.location.replace(loginUrl);
        }
      }
    });

    // Cleanup listener on unmount
    return cleanup;
  }, [requireAuth]);

  return (
    <AuthContext.Provider value={{user, status, signIn, signOut}}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use the authentication context
export function useAuth() {
  return useContext(AuthContext);
}
